import React, { useEffect, useState } from 'react';
import { storage } from '../../../services/StorageService';
import { toggleHotbarCooldownNumbers, useHotbarCooldownNumbers } from '../../Systems/ActionBar/hotbarCooldownNumbersPref';
import {
  OVERHEAD_NAMES_MODES,
  setOverheadNamesMode,
  type OverheadNamesMode,
} from '../../../rendering/overhead/overheadNamesPref';
import { setClassTintNames } from '../../../rendering/overhead/classTintNamesPref';
import { SettingsToggle } from './components/SettingsToggle';
import type { UiPrefs } from '../../../../shared/userprefs/schema';
import { useFullscreen } from '../../hooks/useFullscreen';

/** Settings copy for ui.overheadNames (plan 15 §2.3). */
const OVERHEAD_NAMES_LABELS: Record<OverheadNamesMode, string> = {
  all: 'All',
  smart: 'Smart',
  target: 'Target only',
};

const HUD_STYLE_OPTIONS: Array<{ value: UiPrefs['hudStyle']; label: string }> = [
  { value: 'mix', label: 'Tome Mix (A Frame & Bars + C Tracker)' },
  { value: 'a', label: 'Tome Leather & Gilt (All A)' },
  { value: 'b', label: 'Tome Parchment Slips (All B)' },
  { value: 'c', label: 'Tome Ink & Vine (All C)' },
  { value: 'classic', label: 'Classic Dark Ink & Brass' },
];

const HUD_VIBE_OPTIONS: Array<{ value: UiPrefs['hudVibe']; label: string }> = [
  { value: 'auto', label: 'Follow Zone (Auto)' },
  { value: 'default', label: 'Classic Parchment & Leather' },
  { value: 'grove', label: 'Woodland Grove' },
  { value: 'moonfall', label: 'Moonfall Silver' },
  { value: 'harbour', label: 'Sun-Bleached Harbour' },
];

/**
 * InterfaceTab — Settings → Interface. UI-slice UserPrefs toggles.
 *
 * Resident sections:
 * - Theme & HUD Style (Plan 17 / H1 & H2 modular theme/vibe choice)
 * - Overhead & Target Names
 * - Combat & Cooldowns
 * - Diagnostics & Stability
 */
export const InterfaceTab: React.FC = () => {
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
  const [hudStyle, setHudStyle] = useState<UiPrefs['hudStyle']>(() => storage.loadUserPrefs().ui.hudStyle ?? 'mix');
  const [hudVibe, setHudVibe] = useState<UiPrefs['hudVibe']>(() => storage.loadUserPrefs().ui.hudVibe ?? 'auto');
  const [fctNames, setFctNames] = useState(() => storage.loadUserPrefs().ui.fctAbilityNames);
  const [overheadNames, setOverheadNames] = useState(() => storage.loadUserPrefs().ui.overheadNames);
  const [classTint, setClassTint] = useState(() => storage.loadUserPrefs().ui.classTintNames);
  const [freezePopup, setFreezePopup] = useState(() => storage.loadUserPrefs().dev.persistentFreezePopup);
  const [freezeToast, setFreezeToast] = useState(() => storage.loadUserPrefs().dev.freezeCornerToast);
  const cooldownNumbers = useHotbarCooldownNumbers();

  useEffect(() => {
    const unsub = storage.subscribeUserPrefs((p) => {
      setHudStyle(p.ui.hudStyle ?? 'mix');
      setHudVibe(p.ui.hudVibe ?? 'auto');
      setFctNames(p.ui.fctAbilityNames);
      setOverheadNames(p.ui.overheadNames);
      setClassTint(p.ui.classTintNames);
      setFreezePopup(p.dev.persistentFreezePopup);
      setFreezeToast(p.dev.freezeCornerToast);
    });
    return () => { unsub(); };
  }, []);

  const changeHudStyle = (style: UiPrefs['hudStyle']) => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({ ...cur, ui: { ...cur.ui, hudStyle: style } });
  };

  const changeHudVibe = (vibe: UiPrefs['hudVibe']) => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({ ...cur, ui: { ...cur.ui, hudVibe: vibe } });
  };

  const toggleFctNames = () => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({ ...cur, ui: { ...cur.ui, fctAbilityNames: !cur.ui.fctAbilityNames } });
  };

  const toggleFreezePopup = () => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({
      ...cur,
      dev: { ...cur.dev, persistentFreezePopup: !cur.dev.persistentFreezePopup },
    });
  };

  const toggleFreezeToast = () => {
    const cur = storage.loadUserPrefs();
    storage.saveUserPrefs({
      ...cur,
      dev: { ...cur.dev, freezeCornerToast: !cur.dev.freezeCornerToast },
    });
  };

  return (
    <div>
      {/* ── Theme & HUD Style ────────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="settings-theme-heading">
        <h3 className="settings-section-title" id="settings-theme-heading">Theme & HUD Style</h3>
        <p className="settings-hint">Choose your interface template and zone vibe palette.</p>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">HUD Style / Template</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Select between the Tome Mix, All-A Leather & Gilt, All-B, All-C, or Classic Dark Brass.
              </div>
            </div>
            <select
              value={hudStyle}
              onChange={(e) => changeHudStyle(e.target.value as UiPrefs['hudStyle'])}
              data-testid="settings-hud-style"
              aria-label="HUD Style / Template"
            >
              {HUD_STYLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Tome Vibe Palette</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Follow current zone atmosphere automatically or pin a preferred palette.
              </div>
            </div>
            <select
              value={hudVibe}
              onChange={(e) => changeHudVibe(e.target.value as UiPrefs['hudVibe'])}
              data-testid="settings-hud-vibe"
              aria-label="Tome Vibe Palette"
            >
              {HUD_VIBE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Fullscreen Mode</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Toggle full-screen immersive view on desktop and mobile devices.
              </div>
            </div>
            <SettingsToggle
              checked={isFullscreen}
              onChange={() => toggleFullscreen()}
              data-testid="settings-fullscreen-toggle"
              aria-label="Fullscreen Mode"
            />
          </div>
        </div>
      </section>

      {/* ── Overhead & Target Names ──────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="settings-overhead-heading" style={{ marginTop: 12 }}>
        <h3 className="settings-section-title" id="settings-overhead-heading">Overhead & Target Names</h3>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Overhead names</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Smart: target, bosses, you and your party always; others when hovered or clear of a crowd. Bars always show.
              </div>
            </div>
            <select
              value={overheadNames}
              onChange={(e) => setOverheadNamesMode(e.target.value as OverheadNamesMode)}
              data-testid="settings-overhead-names"
              aria-label="Overhead names"
            >
              {OVERHEAD_NAMES_MODES.map((m) => (
                <option key={m} value={m}>{OVERHEAD_NAMES_LABELS[m]}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Class-coloured names</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Other players’ names over their heads in their calling’s colour. Colour is not friend or foe.
              </div>
            </div>
            <SettingsToggle
              checked={classTint}
              onChange={() => setClassTintNames(!classTint)}
              data-testid="settings-class-tint-names"
              aria-label="Class-coloured names"
            />
          </div>
        </div>
      </section>

      {/* ── Combat & Cooldowns ───────────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="settings-combat-heading" style={{ marginTop: 12 }}>
        <h3 className="settings-section-title" id="settings-combat-heading">Combat & Cooldowns</h3>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Combat text ability names</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Show the source ability under damage numbers; DoT ticks labeled “(tick)”.
              </div>
            </div>
            <SettingsToggle
              checked={fctNames}
              onChange={toggleFctNames}
              data-testid="settings-fct-ability-names"
              aria-label="Combat text ability names"
            />
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Hotbar cooldown numbers</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Seconds left on each ability cooling down (tenths under 3 s). The sweep shows either way.
              </div>
            </div>
            <SettingsToggle
              checked={cooldownNumbers}
              onChange={toggleHotbarCooldownNumbers}
              data-testid="settings-hotbar-cooldown-numbers"
              aria-label="Hotbar cooldown numbers"
            />
          </div>
        </div>
      </section>

      {/* ── Diagnostics & Stability ──────────────────────────────────── */}
      <section className="settings-section" aria-labelledby="settings-diag-heading" style={{ marginTop: 12 }}>
        <h3 className="settings-section-title" id="settings-diag-heading">Diagnostics & Stability</h3>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Persistent freeze error popup</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Center-screen freeze details with recent netcode events. Stays open until dismissed.
              </div>
            </div>
            <SettingsToggle
              checked={freezePopup}
              onChange={toggleFreezePopup}
              data-testid="settings-persistent-freeze-popup"
              aria-label="Persistent freeze error popup"
            />
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-card-row">
            <div>
              <div className="settings-card-label">Freeze corner tag</div>
              <div className="settings-hint" style={{ margin: '2px 0 0' }}>
                Brief bottom-right “⏱ freeze 166ms · client” flash. Detection and logging continue when OFF.
              </div>
            </div>
            <SettingsToggle
              checked={freezeToast}
              onChange={toggleFreezeToast}
              data-testid="settings-freeze-corner-toast"
              aria-label="Freeze corner tag"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
